/**
 * Helpers for the superAdmin system-defaults editor.
 *
 * Stored defaults are a sparse override of the code registry `defaultEnabled`.
 * Resolution order on the API is: parent-company override, then tier default,
 * then system default, then the code registry.
 */

/** @type {Readonly<Record<string, string>>} */
const FEATURE_FLAG_SOURCE_LABELS = Object.freeze({
  override: "parent company",
  tier: "tier default",
  system: "system default",
  registry: "code default",
  lifecycle: "lifecycle",
  grandfather: "grandfather",
});

/**
 * Human label for a resolver source key.
 *
 * @param {string} [source]
 * @returns {string}
 */
export function formatFeatureFlagSource(source) {
  if (!source) return "";
  return FEATURE_FLAG_SOURCE_LABELS[source] || String(source);
}

/**
 * Whether the system-defaults map explicitly sets this flag.
 *
 * @param {string} featureKey
 * @param {Record<string, boolean>|null|undefined} storedDefaults
 * @returns {boolean}
 */
export function isSystemDefaultOverridden(featureKey, storedDefaults) {
  return Boolean(
    storedDefaults &&
      Object.prototype.hasOwnProperty.call(storedDefaults, featureKey)
  );
}

/**
 * Effective baseline for a flag: stored system default, otherwise the code default.
 *
 * @param {string} featureKey
 * @param {{ defaultEnabled?: boolean }|null|undefined} meta
 * @param {Record<string, boolean>|null|undefined} storedDefaults
 * @returns {boolean}
 */
export function getEffectiveSystemDefault(featureKey, meta, storedDefaults) {
  if (isSystemDefaultOverridden(featureKey, storedDefaults)) {
    return Boolean(storedDefaults[featureKey]);
  }
  return Boolean(meta?.defaultEnabled);
}

/**
 * Return a new sparse map after toggling one flag.
 * Values that match the code default are removed so the map stays an override list.
 *
 * @param {Record<string, boolean>|null|undefined} storedDefaults
 * @param {string} featureKey
 * @param {{ defaultEnabled?: boolean }|null|undefined} meta
 * @param {boolean} enabled
 * @returns {Record<string, boolean>}
 */
export function withSystemDefaultToggle(
  storedDefaults,
  featureKey,
  meta,
  enabled
) {
  const next = { ...(storedDefaults || {}) };
  const codeDefault = Boolean(meta?.defaultEnabled);
  if (Boolean(enabled) === codeDefault) {
    delete next[featureKey];
    return next;
  }
  next[featureKey] = Boolean(enabled);
  return next;
}
